
import { NetworkInfo, NetworkInformation } from './types';

/**
 * Handles network condition detection and monitoring
 */
export class NetworkDetector {
  private networkInfo: NetworkInfo = {
    isLowBandwidth: false,
    lastChecked: 0
  };

  /**
   * Checks current network conditions with safe feature detection
   */
  public checkNetworkConditions(): void {
    try {
      const now = Date.now();
      
      // Only check every 60 seconds to avoid excessive checks
      if (now - this.networkInfo.lastChecked < 60000) {
        return;
      }

      this.networkInfo.lastChecked = now;

      // Safely check for Navigator Connection API
      if (navigator && 'connection' in navigator && (navigator as any).connection) {
        const connection = (navigator as any).connection as NetworkInformation;
        
        // Make sure effectiveType exists before checking its value
        if (connection.effectiveType) {
          if (connection.effectiveType === '2g' || connection.effectiveType === 'slow-2g') {
            this.networkInfo.isLowBandwidth = true;
          } else {
            this.networkInfo.isLowBandwidth = false;
          }
        } else {
          // Fallback if effectiveType isn't available
          this.networkInfo.isLowBandwidth = false;
        }
      } else {
        // Use Performance API as fallback, with safe checks
        if (typeof performance !== 'undefined' && performance.getEntriesByType) {
          const navEntries = performance.getEntriesByType('navigation');
          if (navEntries && navEntries.length > 0) {
            // Use newer Navigation Timing API (Level 2)
            const navTiming = navEntries[0] as PerformanceNavigationTiming;
            if (navTiming.responseStart && navTiming.requestStart) {
              const responseTime = navTiming.responseStart - navTiming.requestStart;
              this.networkInfo.isLowBandwidth = responseTime > 1000;
            }
          } else if (performance.timing) {
            // Fallback to older Performance Timing API (deprecated but still supported)
            const timing = performance.timing;
            if (timing.responseEnd && timing.requestStart) {
              const responseTime = timing.responseEnd - timing.requestStart;
              this.networkInfo.isLowBandwidth = responseTime > 1000;
            }
          } else {
            // Default to false if no timing information is available
            this.networkInfo.isLowBandwidth = false;
          }
        } else {
          // No performance API available, assume good connection
          this.networkInfo.isLowBandwidth = false;
        }
      }
    } catch (error) {
      // Handle any unexpected errors gracefully
      console.error("Error detecting network conditions:", error);
      this.networkInfo.isLowBandwidth = false; // Default to false on error
    }
  }

  /**
   * Gets current network status, with safe initialization
   */
  public getNetworkStatus(): { isLowBandwidth: boolean } {
    try {
      this.checkNetworkConditions();
    } catch (error) {
      console.error("Failed to check network conditions:", error);
      // In case of error, default to false
      this.networkInfo.isLowBandwidth = false;
    }
    return { isLowBandwidth: this.networkInfo.isLowBandwidth };
  }
}
